// ============================================================================
// HOOPS FOR THOUGHT: CONTRACT MARKET ENGINE
// Generates realistic AI free agent offers based on comparable contracts
// ============================================================================

import SALARY_DATA from './salaryData';
import { getPlayerRating } from './playerRatings';
import { CBA } from './tradeEngine';

// ============================================================================
// MARKET MODEL — Built from all current NBA contracts
// Uses OVR rating as primary driver (most reliable signal we have)
// ============================================================================

/**
 * Build market comparables dataset from salary data
 * Groups contracts by OVR, calculates percentiles
 */
function buildMarketData(salaryData) {
  const contracts = [];
  const data = salaryData || SALARY_DATA;

  for (const [abbr, team] of Object.entries(data)) {
    if (!team?.players) continue;
    for (const p of team.players) {
      if (p.isDead || p.isTwoWay) continue;
      const ovr = getPlayerRating(p.name);
      if (!ovr || !p.salary2526) continue;
      contracts.push({ ovr, salary: p.salary2526 });
    }
  }
  return contracts;
}

/**
 * Get market value for a given OVR rating
 * Uses weighted average of nearby OVR contracts (±3 range)
 * Returns { low, mid, high, max } salary estimates
 */
export function getMarketValue(ovr, salaryData) {
  const contracts = buildMarketData(salaryData);
  const range = 3;

  // Get all contracts within OVR range
  const comps = contracts.filter(c => Math.abs(c.ovr - ovr) <= range);

  if (comps.length === 0) {
    // Fallback: use tier-based estimates
    return getFallbackValue(ovr);
  }

  // Weight by closeness to target OVR
  const weighted = comps.map(c => ({
    salary: c.salary,
    weight: 1 / (1 + Math.abs(c.ovr - ovr)),
  }));

  const totalWeight = weighted.reduce((s, w) => s + w.weight, 0);
  const weightedAvg = weighted.reduce((s, w) => s + w.salary * w.weight, 0) / totalWeight;

  // Sort for percentiles
  const sorted = comps.map(c => c.salary).sort((a, b) => a - b);
  const p25 = sorted[Math.floor(sorted.length * 0.25)] || sorted[0];
  const p50 = sorted[Math.floor(sorted.length * 0.50)] || sorted[0];
  const p75 = sorted[Math.floor(sorted.length * 0.75)] || sorted[0];
  const p90 = sorted[Math.floor(sorted.length * 0.90)] || sorted[sorted.length - 1];

  return {
    low: Math.round(p25),
    mid: Math.round(p50),
    high: Math.round(p75),
    ceiling: Math.round(p90),
    weightedAvg: Math.round(weightedAvg),
    comps: comps.length,
  };
}

function getFallbackValue(ovr) {
  // Tier-based fallback (from our dataset analysis)
  if (ovr >= 95) return { low: 35000000, mid: 47000000, high: 55000000, ceiling: 60000000, weightedAvg: 47000000, comps: 0 };
  if (ovr >= 90) return { low: 35000000, mid: 42000000, high: 50000000, ceiling: 55000000, weightedAvg: 42000000, comps: 0 };
  if (ovr >= 85) return { low: 20000000, mid: 29000000, high: 38000000, ceiling: 50000000, weightedAvg: 29000000, comps: 0 };
  if (ovr >= 80) return { low: 8000000, mid: 16000000, high: 24000000, ceiling: 35000000, weightedAvg: 16000000, comps: 0 };
  if (ovr >= 75) return { low: 3000000, mid: 8000000, high: 14000000, ceiling: 20000000, weightedAvg: 8000000, comps: 0 };
  if (ovr >= 70) return { low: 2000000, mid: 3500000, high: 6000000, ceiling: 10000000, weightedAvg: 3500000, comps: 0 };
  return { low: 1800000, mid: 2100000, high: 2500000, ceiling: 3000000, weightedAvg: 2100000, comps: 0 };
}

// ============================================================================
// AI OFFER GENERATION
// Simulates other teams making offers to free agents
// ============================================================================

// Teams that typically spend in FA (cap space or willingness)
const FA_AGGRESSIVE_TEAMS = ['BKN', 'DET', 'SAS', 'UTA', 'CHA', 'POR', 'WAS'];
const FA_MODERATE_TEAMS = ['ORL', 'OKC', 'HOU', 'SAC', 'IND', 'ATL', 'CHI', 'NOP', 'TOR'];
const FA_CONSERVATIVE_TEAMS = ['BOS', 'MIL', 'PHX', 'GSW', 'DAL', 'LAC', 'DEN', 'MIN', 'CLE', 'NYK'];

/**
 * Generate 1–3 AI contract offers for a free agent
 * Takes into account: OVR, prior salary, market value, positional need
 */
export function generateOffers(freeAgent, salaryData, userTeam) {
  const ovr = freeAgent.ovr || getPlayerRating(freeAgent.name) || 70;
  const market = getMarketValue(ovr, salaryData);
  const priorSalary = freeAgent.priorSalary || 0;

  // Estimate age from OVR trends (young stars > old vets)
  // Without exact age, we use OVR as a proxy for desirability
  const isHighValue = ovr >= 80;
  const isMidValue = ovr >= 73 && ovr < 80;

  // How many offers? Better players get more interest
  let numOffers;
  if (ovr >= 85) numOffers = 3;
  else if (ovr >= 78) numOffers = 2 + (Math.random() > 0.5 ? 1 : 0);
  else if (ovr >= 72) numOffers = 1 + (Math.random() > 0.4 ? 1 : 0);
  else numOffers = Math.random() > 0.6 ? 1 : 0;

  // Cap at 3
  numOffers = Math.min(numOffers, 3);

  // Pick offering teams (exclude user's team — they make their own offer)
  const allTeams = [...FA_AGGRESSIVE_TEAMS, ...FA_MODERATE_TEAMS, ...FA_CONSERVATIVE_TEAMS]
    .filter(t => t !== userTeam);

  // Weighted random: aggressive teams more likely to offer
  const weightedTeams = [
    ...FA_AGGRESSIVE_TEAMS.filter(t => t !== userTeam).map(t => ({ team: t, weight: 3 })),
    ...FA_MODERATE_TEAMS.filter(t => t !== userTeam).map(t => ({ team: t, weight: 2 })),
    ...FA_CONSERVATIVE_TEAMS.filter(t => t !== userTeam).map(t => ({ team: t, weight: 1 })),
  ];

  // Check which teams have roster space + cap room
  const eligibleTeams = weightedTeams.filter(({ team }) => {
    const td = (salaryData || SALARY_DATA)[team];
    if (!td) return true;
    const rosterCount = (td.players || []).filter(p => !p.isDead && !p.isTwoWay).length;
    if (rosterCount >= 15) return false; // Full roster
    return true;
  });

  // Select random teams weighted by aggressiveness
  const selectedTeams = [];
  const pool = [...eligibleTeams];
  for (let i = 0; i < numOffers && pool.length > 0; i++) {
    const totalWeight = pool.reduce((s, t) => s + t.weight, 0);
    let r = Math.random() * totalWeight;
    let pick = pool[0];
    for (const t of pool) {
      r -= t.weight;
      if (r <= 0) { pick = t; break; }
    }
    selectedTeams.push(pick.team);
    pool.splice(pool.indexOf(pick), 1);
  }

  // Generate offers
  const offers = selectedTeams.map(team => {
    const td = (salaryData || SALARY_DATA)[team];
    const payroll = td?.payroll || 0;
    const capSpace = Math.max(0, CBA.salaryCap - payroll);
    const hasCapSpace = capSpace > 5000000;
    const isAggressive = FA_AGGRESSIVE_TEAMS.includes(team);

    // Salary: varies around market mid
    let offerSalary;
    const variance = 0.85 + Math.random() * 0.30; // 85%–115% of market

    if (hasCapSpace && isAggressive) {
      // Aggressive + cap space = overpay
      offerSalary = Math.round(market.high * variance);
    } else if (hasCapSpace) {
      // Cap space team = market rate
      offerSalary = Math.round(market.mid * variance);
    } else {
      // Over cap = exception money only
      const mle = 14167500; // Non-taxpayer MLE
      offerSalary = Math.min(Math.round(market.mid * variance), mle);
    }

    // Floor: vet minimum
    offerSalary = Math.max(offerSalary, 2100000);

    // Cap: can't exceed max salary
    const maxSal = Math.floor(CBA.salaryCap * 0.35);
    offerSalary = Math.min(offerSalary, maxSal);

    // Years: better players get longer deals
    let years;
    if (ovr >= 85) years = 3 + Math.floor(Math.random() * 2); // 3-4
    else if (ovr >= 78) years = 2 + Math.floor(Math.random() * 2); // 2-3
    else if (ovr >= 72) years = 1 + Math.floor(Math.random() * 2); // 1-2
    else years = 1;

    // Max years from exception type
    if (!hasCapSpace) years = Math.min(years, 2); // MLE = 2yr max typically

    // Calculate total with 5% annual raises
    const annual = [];
    for (let y = 0; y < years; y++) {
      annual.push(Math.round(offerSalary * Math.pow(1.05, y)));
    }
    const total = annual.reduce((s, v) => s + v, 0);

    return {
      team,
      salary: offerSalary,
      years,
      annual,
      total,
      via: hasCapSpace ? 'Cap Space' : 'Mid-Level Exception',
      isOverpay: offerSalary > market.high,
    };
  });

  // Sort by total value descending (best offer first)
  offers.sort((a, b) => b.total - a.total);

  return {
    player: freeAgent.name,
    ovr,
    market,
    offers,
    noOffers: offers.length === 0,
  };
}

// ============================================================================
// USER OFFER VALIDATION
// Checks if user's offer is valid given their cap situation
// ============================================================================

/**
 * Determine what signing tools the user has available
 */
export function getUserSigningOptions(userTeam, salaryData) {
  const td = (salaryData || SALARY_DATA)[userTeam];
  if (!td) return { capSpace: 0, tools: [] };

  const payroll = td.payroll || 0;
  const capSpace = Math.max(0, CBA.salaryCap - payroll);
  const hasCapSpace = capSpace > 2100000;
  const inTax = payroll > CBA.luxuryTax;

  const tools = [];

  if (hasCapSpace) {
    tools.push({
      type: 'capSpace',
      label: 'Cap Space',
      maxSalary: capSpace,
      maxYears: 4,
    });
  }

  // Non-taxpayer MLE (over cap, below first apron)
  if (!hasCapSpace && payroll < CBA.firstApron) {
    tools.push({
      type: 'mle',
      label: 'Non-Taxpayer MLE',
      maxSalary: 14167500,
      maxYears: 4,
    });
  }

  // Taxpayer MLE
  if (inTax && payroll < CBA.secondApron) {
    tools.push({
      type: 'tmle',
      label: 'Taxpayer MLE',
      maxSalary: 5178000,
      maxYears: 2,
    });
  }

  // Vet minimum (always available)
  tools.push({
    type: 'vetMin',
    label: 'Veteran Minimum',
    maxSalary: 2461463,
    maxYears: 2,
  });

  // Bird rights (for re-signing own players — future enhancement)
  // Would check if player was previously on this team

  return { capSpace, tools, payroll, inTax };
}

/**
 * Check if user's offer beats the best AI offer
 */
export function canSignPlayer(userOffer, aiOffers) {
  if (!aiOffers || aiOffers.length === 0) return true;

  const bestAI = aiOffers[0]; // Already sorted by total
  // Player goes to highest total value offer
  return userOffer.total >= bestAI.total;
}

/**
 * Calculate total value of a contract offer
 */
export function calculateOfferTotal(salary, years, raise = 0.05) {
  let total = 0;
  for (let y = 0; y < years; y++) {
    total += Math.round(salary * Math.pow(1 + raise, y));
  }
  return total;
}

/**
 * Determine what happens if user doesn't act on a free agent
 * The AI's best offer wins, and the player signs there
 */
export function resolveUnsignedFA(freeAgent, offers) {
  if (!offers || offers.length === 0) {
    // Nobody wants them — they remain unsigned
    return { signed: false, player: freeAgent.name };
  }

  const bestOffer = offers[0];
  return {
    signed: true,
    player: freeAgent.name,
    team: bestOffer.team,
    salary: bestOffer.salary,
    years: bestOffer.years,
    total: bestOffer.total,
  };
}

export default {
  getMarketValue,
  generateOffers,
  getUserSigningOptions,
  canSignPlayer,
  calculateOfferTotal,
  resolveUnsignedFA,
};
